import React, { useEffect, useRef, useState } from 'react';
import './leaderboard-settings.styles.scss';
import flatpickr from 'flatpickr';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { useSelector } from 'react-redux';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faCalendarDay, faChampagneGlasses, faLineChart, faMedal } from '@fortawesome/free-solid-svg-icons';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';

const LeaderboardSettings = ({
    liveData,
    leaderData,
    showFullLeaderboard
}) => {

    const userProfile = useSelector(state => state.userData.userProfile);
    const screenSize = useSelector(state => state.contentData.screenSize);
    const [ alertMessage, setAlertMessage ] = useState(null);
    const [ confirmMessage, setConfirmMessage ] = useState(null);
    const [ baselineLoading, setBaselineLoading ] = useState(false);
    const [ dateLoading, setDateLoading ] = useState(false);
    const date1Ref = useRef();
    const endDateRef = useRef();
    const approveRef = useRef();

    useEffect(() => {
        if (liveData.data !== 'change') return;
        const endDateElement = document.getElementById('end-date');
        if (!date1Ref.current) {
            const flat1 = flatpickr(endDateElement, 
                {
                    enableTime: true,
                    dateFormat: "F j, Y, h:i K",
                    onChange: (dates) => {
                        const date = new Date(dates[0]).getTime();
                        endDateRef.current = date;
                    }
                }
            );
            date1Ref.current = flat1;
        }
    }, [])

    useEffect(() => {
        if (!liveData || !date1Ref.current) return;
        if (liveData.opt_dateEnd) {
            date1Ref.current.setDate(new Date(liveData.opt_dateEnd));
        }
    }, [liveData, date1Ref.current])

    async function saveLeaderboardSettings() {
        setDateLoading(true);
        let tempData = {...liveData};
        if (endDateRef.current) {
            tempData.opt_dateEnd = endDateRef.current;
        }
        const res = await UseCloudFunction(
            'saveGameElement',
            {
                'appId': userProfile.appId,
                'elementData': tempData,
                'userName': userProfile.name
            }
        )
        console.log(res);
        setDateLoading(false);
    }

    function setLeaderboardBaseline() {
        setConfirmMessage('Are you sure you want to set the baseline for this Leaderboard? This will overwrite any existing baselines and start your players off with no change. This action cannot be undone.')
        approveRef.current = () => {
            continueBaselineSet();
        }

        async function continueBaselineSet() {
            setBaselineLoading(true);
            const res = await UseCloudFunction(
                'setLeaderboardBaseline',
                {
                    'leaderboard': liveData,
                    'leaderData': leaderData,
                    'appId': userProfile.appId
                }
            )
            console.log(res);
            if (res.error) {
                setAlertMessage(`Something went wrong. Please try again later. (Error: ${res.error})`);
                return;
            }
            setAlertMessage('Baseline set successfully!');
            setBaselineLoading(false);
            return;
        }
    }

    return (
        <div className={`leaderboard-settings ${screenSize}`}>
            {
                (liveData.data === 'change') &&
                <div>
                    <div className='field double-col'>
                        <div className='date-text'>
                            <h3><FontAwesomeIcon icon={faLineChart} />Baseline</h3>
                            Click the button below to set a new baseline for measuring change. 
                            It will set the baseline with your players' current data. 
                            If a baseline has already been set, this will overwrite it.
                        </div>
                        <div className='date-choice'>
                            <div>
                                {
                                    (baselineLoading)
                                    ?
                                    <button className='g-button med-btn'>
                                        <ThreeDotsLoader />
                                    </button>
                                    :
                                    <button 
                                        className='g-button med-btn'
                                        onClick={() => setLeaderboardBaseline()}>
                                        Set Baseline
                                    </button>
                                }
                            </div>
                        </div>
                    </div>
                    <hr />
                    <div className='field small double-col'>
                        <div className='date-text'>
                            <h3><FontAwesomeIcon icon={faCalendarDay} />End Tracking Date</h3>
                            Set the date and time to stop tracking the change. 
                            The leaderboard will still be accessible to you and your players 
                            until you make it unavailable, but it will not be tracking 
                            changes after the chosen date.
                        </div>
                        <div className='date-choice'>
                            <input id='end-date' placeholder='Choose an end date...' className='short-input' />
                            <div>
                                {
                                    (dateLoading) 
                                    ?
                                    <button className='g-button med-btn'>
                                        <ThreeDotsLoader />
                                    </button>
                                    :
                                    <button 
                                        className='g-button med-btn'
                                        onClick={() => saveLeaderboardSettings()}>
                                        Save End Date
                                    </button>
                                
                                }
                            </div>
                        </div>
                    </div>
                    <hr />
                </div>
            }
            <div>
                <div className='field small double-col'>
                    <div className='date-text'>
                        <h3>
                            <FontAwesomeIcon icon={faMedal} />
                            Show Full Leaderboard
                        </h3>
                        Click this button to display the full leaderboard. This change will 
                        not affect the public leaderboard or the board your players see.
                    </div>
                    <div className='date-choice'>
                        <div>
                            <button 
                                className='g-button med-btn'
                                onClick={() => showFullLeaderboard()}>
                                Show Full Leaderboard
                            </button>
                        </div>
                    </div>

                </div>
            </div>
            <ModalAlert
                show={alertMessage}
                cancel={() => setAlertMessage(null)}
                message={alertMessage} />
            <ModalConfirm 
                show={confirmMessage}
                cancel={() => setConfirmMessage(null)}
                message={confirmMessage}
                onApprove={approveRef.current} />
            
        </div>
    )
}

export default LeaderboardSettings